import type { CSSProperties } from "react";
import { cn } from "@/lib/utils";
import { blurhashToDataURL } from "@/lib/image-hash";
import { ImageClient } from "./image-client";

export interface SrcImageProps {
	src?: string;
	alt?: string;
	width?: number;
	height?: number;
	blurhash?: string | null;
	disableBlurhash?: boolean;
	className?: string;
	wrapperClassName?: string;
	priorityLoad?: boolean;
	fill?: boolean;
	style?: CSSProperties;
}

/**
 * Renders an image from a direct URL.
 *
 * @param props - Component props.
 * @returns The rendered image element or null if no valid image source is provided.
 */
export const SrcImage: React.FC<SrcImageProps> = ({
	src,
	alt,
	width,
	height,
	blurhash,
	disableBlurhash = false,
	className,
	wrapperClassName,
	priorityLoad = false,
	fill,
	style,
}) => {
	if (!src) {
		return null;
	}

	let blurWidth: number | undefined;
	let blurHeight: number | undefined;

	if (width && height) {
		const blurRatio = width / height;
		const blurPlaceholderSize = 24;

		if (blurRatio >= 1) {
			blurWidth = blurPlaceholderSize;
			blurHeight = Math.max(1, Math.round(blurPlaceholderSize / blurRatio));
		} else {
			blurWidth = Math.max(1, Math.round(blurPlaceholderSize * blurRatio));
			blurHeight = blurPlaceholderSize;
		}
	}

	const blurDataURL =
		!disableBlurhash && blurhash && blurWidth && blurHeight && !src.toLowerCase().split("?")[0]?.endsWith(".svg")
			? blurhashToDataURL(blurhash, blurWidth, blurHeight)
			: undefined;

	const imageElement = (
		<div className="blurhash bg-cover bg-center bg-no-repeat" style={blurDataURL ? { backgroundImage: `url("${blurDataURL}")` } : undefined}>
			<ImageClient
				loading={priorityLoad ? "eager" : "lazy"}
				fetchPriority={priorityLoad ? "high" : undefined}
				priority={priorityLoad}
				src={src}
				alt={alt || ""}
				width={width}
				height={height}
				className={cn("w-full h-auto", !priorityLoad && "transition duration-200", className)}
				fill={fill}
				style={wrapperClassName ? undefined : style}
			/>
		</div>
	);

	if (wrapperClassName) {
		return (
			<figure className={cn(wrapperClassName)} style={style}>
				{imageElement}
			</figure>
		);
	}

	return imageElement;
};
